/**
 * AuditLogLive.tsx — Sprint 26.3
 *
 * Real-time audit log panel. Attempts to connect to the SSE stream at
 * GET /admin/audit-log/stream. If that endpoint returns 404/501 (not yet
 * built by backend), falls back automatically to 5-second polling against
 * GET /admin/audit-log.
 *
 * Backend requirement:
 *   GET /admin/audit-log/stream  (SSE; moderator+) — Sprint 26.1 / 27
 *   See docs/design/admin-oauth-login-spec.md §6 for the Sprint 27 follow-up list.
 *
 * Features:
 *   - Last 50 entries, most recent first
 *   - New entries animate in at the top (CSS slide-down)
 *   - Filter chips: actor_role, action, target_type
 *   - Click a row to expand changes_json
 *   - Mode indicator: SSE (live) or polling (fallback)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { listAuditLog, openAuditLogStream, AuditLogEntry } from '../api/admin-client';

const MAX_ENTRIES = 50;
const POLL_INTERVAL_MS = 5_000;

// ─── Filter chip data ─────────────────────────────────────────────────────────

const ROLE_CHIPS = ['system', 'mod', 'admin', 'super_admin'] as const;
type RoleChip = typeof ROLE_CHIPS[number];

const ACTION_CHIPS = [
  'account.ban',
  'account.suspend',
  'account.unsuspend',
  'account.warn',
  'report.resolve',
  'shard.restart',
  'admin.role_grant',
  'stripe.dead_letter.retry',
  'stripe.dead_letter.dismiss',
  'admin.oauth_login',
] as const;
type ActionChip = typeof ACTION_CHIPS[number];

const TARGET_TYPE_CHIPS = ['account', 'report', 'shard', 'stripe_event', 'admin'] as const;
type TargetTypeChip = typeof TARGET_TYPE_CHIPS[number];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function actionBadge(action: string) {
  if (action.includes('ban'))      return <span className="badge badge-red">{action}</span>;
  if (action.includes('suspend'))  return <span className="badge badge-amber">{action}</span>;
  if (action.includes('warn'))     return <span className="badge badge-amber">{action}</span>;
  if (action.includes('dismiss'))  return <span className="badge badge-gray">{action}</span>;
  if (action.includes('grant'))    return <span className="badge badge-blue">{action}</span>;
  if (action.includes('login'))    return <span className="badge badge-green">{action}</span>;
  return <span className="badge badge-blue">{action}</span>;
}

function roleBadge(role?: string) {
  if (!role) return <span className="badge badge-gray">system</span>;
  if (role === 'super_admin') return <span className="badge badge-red">{role}</span>;
  if (role === 'admin')       return <span className="badge badge-blue">{role}</span>;
  if (role === 'moderator' || role === 'mod') return <span className="badge badge-amber">mod</span>;
  return <span className="badge badge-gray">{role}</span>;
}

// ─── Animated entry row ───────────────────────────────────────────────────────

function EntryRow({
  entry,
  isNew,
  expanded,
  onToggle,
}: {
  entry: AuditLogEntry & { actor_role?: string };
  isNew: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const hasChanges =
    entry.changes_json != null &&
    Object.keys(entry.changes_json ?? {}).length > 0;

  return (
    <>
      <tr
        key={entry.log_id}
        className={isNew ? 'audit-row-new' : ''}
        onClick={hasChanges ? onToggle : undefined}
        style={{ cursor: hasChanges ? 'pointer' : 'default' }}
        title={hasChanges ? 'Click to expand changes' : undefined}
      >
        <td className="text-muted" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>
          <span title={entry.timestamp}>{timeAgo(entry.timestamp)}</span>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
            {new Date(entry.timestamp).toLocaleTimeString()}
          </div>
        </td>
        <td style={{ minWidth: 80 }}>
          {roleBadge((entry as any).actor_role)}
        </td>
        <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.actor_email
            ? <span title={entry.actor_account_id}>{entry.actor_email}</span>
            : entry.actor_account_id
              ? <span className="text-mono" style={{ fontSize: 11 }}>{entry.actor_account_id.slice(0, 12)}…</span>
              : <span className="text-muted">system</span>
          }
        </td>
        <td>{actionBadge(entry.action)}</td>
        <td>
          {entry.target_type && (
            <span className="badge badge-gray" style={{ marginRight: 4 }}>
              {entry.target_type}
            </span>
          )}
          {entry.target_id && (
            <span className="text-mono" style={{ fontSize: 11 }}>
              {entry.target_id.slice(0, 12)}{entry.target_id.length > 12 ? '…' : ''}
            </span>
          )}
          {!entry.target_type && !entry.target_id && (
            <span className="text-muted">—</span>
          )}
        </td>
        <td style={{ textAlign: 'right' }}>
          {hasChanges && (
            <span className="text-muted" style={{ fontSize: 11 }}>
              {expanded ? '▲ hide' : '▼ show'}
            </span>
          )}
        </td>
      </tr>
      {expanded && hasChanges && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <pre className="raw-payload">
              {JSON.stringify(entry.changes_json, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type LiveEntry = AuditLogEntry & { actor_role?: string; _isNew?: boolean };

type ConnectionMode = 'connecting' | 'sse' | 'polling' | 'error';

export default function AuditLogLive() {
  const [entries, setEntries] = useState<LiveEntry[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<ConnectionMode>('connecting');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  // Filter chips (multi-select: empty set = no filter)
  const [activeRoles, setActiveRoles]      = useState<Set<RoleChip>>(new Set());
  const [activeActions, setActiveActions]  = useState<Set<ActionChip>>(new Set());
  const [activeTargets, setActiveTargets]  = useState<Set<TargetTypeChip>>(new Set());

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());

  // ── Filter logic ────────────────────────────────────────────────────────────

  function matchesFilters(e: LiveEntry): boolean {
    if (activeRoles.size > 0) {
      const role = (e as any).actor_role ?? 'system';
      // normalise 'moderator' → 'mod' for chip matching
      const normRole = role === 'moderator' ? 'mod' : role;
      if (!activeRoles.has(normRole as RoleChip)) return false;
    }
    if (activeActions.size > 0) {
      if (!activeActions.has(e.action as ActionChip)) return false;
    }
    if (activeTargets.size > 0) {
      if (!activeTargets.has((e.target_type ?? '') as TargetTypeChip)) return false;
    }
    return true;
  }

  const visibleEntries = entries.filter(matchesFilters);

  // ── Merge new entries (dedup by log_id) ─────────────────────────────────────

  function mergeEntries(incoming: LiveEntry[]) {
    setEntries(prev => {
      const next = [...prev];
      let added = false;
      for (const entry of incoming) {
        const key = entry.log_id ?? entry.timestamp + entry.action;
        if (!seenIdsRef.current.has(key)) {
          seenIdsRef.current.add(key);
          next.unshift({ ...entry, _isNew: true });
          added = true;
        }
      }
      if (!added) return prev;
      // Clear _isNew flag after animation delay
      setTimeout(() => {
        setEntries(cur => cur.map(e => ({ ...e, _isNew: false })));
      }, 1200);
      // Trim to MAX_ENTRIES
      return next.slice(0, MAX_ENTRIES);
    });
    setLastUpdate(new Date());
  }

  // ── Initial load via polling endpoint ───────────────────────────────────────

  const loadInitial = useCallback(async () => {
    try {
      const result = await listAuditLog({ limit: MAX_ENTRIES, offset: 0 });
      const initial = (result.entries ?? []).map(e => ({ ...e, _isNew: false }));
      for (const e of initial) {
        const key = e.log_id ?? e.timestamp + e.action;
        seenIdsRef.current.add(key);
      }
      setEntries(initial);
      setLastUpdate(new Date());
      setEndpointMissing(false);
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 501 || err?.code === 'not_found') {
        setEndpointMissing(true);
      }
      // Non-fatal — mode will still be set below
    }
  }, []);

  // ── Poll fallback (5s) ───────────────────────────────────────────────────────

  const pollOnce = useCallback(async () => {
    try {
      const result = await listAuditLog({ limit: MAX_ENTRIES, offset: 0 });
      const incoming = (result.entries ?? []) as LiveEntry[];
      mergeEntries(incoming);
      setEndpointMissing(false);
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 501 || err?.code === 'not_found') {
        setEndpointMissing(true);
      }
    }
  }, []);

  function startPolling() {
    setMode('polling');
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
  }

  // ── SSE connection ───────────────────────────────────────────────────────────

  function trySSE() {
    setMode('connecting');
    let es: EventSource;
    try {
      es = openAuditLogStream();
    } catch {
      // EventSource constructor can throw if URL is invalid
      startPolling();
      return;
    }
    sseRef.current = es;

    es.addEventListener('open', () => {
      setMode('sse');
    });

    es.addEventListener('audit_entry', (evt: MessageEvent) => {
      try {
        const entry: LiveEntry = JSON.parse(evt.data);
        mergeEntries([entry]);
      } catch {
        // ignore malformed event
      }
    });

    es.addEventListener('error', () => {
      es.close();
      sseRef.current = null;
      // 404/501 from the SSE endpoint falls here — fall back to polling
      startPolling();
    });
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadInitial().then(() => {
      trySSE();
    });

    return () => {
      sseRef.current?.close();
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // ── Toggle helpers ───────────────────────────────────────────────────────────

  function toggleExpanded(key: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleChip<T extends string>(set: Set<T>, val: T, setter: (s: Set<T>) => void) {
    const next = new Set(set);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    setter(next);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const modeLabel =
    mode === 'sse'        ? 'Live (SSE)' :
    mode === 'polling'    ? 'Polling (5s fallback)' :
    mode === 'connecting' ? 'Connecting…' :
                            'Error';

  const modeBadgeClass =
    mode === 'sse'     ? 'badge-green' :
    mode === 'polling' ? 'badge-amber' :
                         'badge-gray';

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="page-title" style={{ marginBottom: 0 }}>Live Audit Log</div>
        <span className={`badge ${modeBadgeClass}`} style={{ fontSize: 11 }}>
          {modeLabel}
        </span>
        {lastUpdate && (
          <span className="text-muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
            Last update: {lastUpdate.toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* SSE stub banner */}
      {mode === 'polling' && (
        <div className="stub-banner">
          <strong>Polling fallback active.</strong> Backend endpoint{' '}
          <code>GET /admin/audit-log/stream</code> (SSE) is not yet live —
          Sprint 26.1 / Sprint 27 backend TODO. Refreshing every 5 seconds via{' '}
          <code>GET /admin/audit-log</code> instead.
        </div>
      )}

      {/* Endpoint missing banner */}
      {endpointMissing && (
        <div className="stub-banner">
          <strong>TODO (Sprint 26.1 backend):</strong> Endpoint{' '}
          <code>GET /admin/audit-log</code> is not yet exposed via HTTP.
          Add it to the admin HTTP layer. Expected query params:{' '}
          <code>since</code>, <code>action</code>, <code>actor</code>,{' '}
          <code>limit</code>, <code>offset</code>.
        </div>
      )}

      {/* Filter chips */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {/* Actor role */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 72 }}>Actor role</span>
          {ROLE_CHIPS.map(r => (
            <button
              key={r}
              className={`btn btn-sm ${activeRoles.has(r) ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => toggleChip(activeRoles, r, setActiveRoles)}
            >
              {r}
            </button>
          ))}
          {activeRoles.size > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={() => setActiveRoles(new Set())}>
              Clear
            </button>
          )}
        </div>

        {/* Action */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 72 }}>Action</span>
          {ACTION_CHIPS.map(a => (
            <button
              key={a}
              className={`btn btn-sm ${activeActions.has(a) ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => toggleChip(activeActions, a, setActiveActions)}
            >
              {a}
            </button>
          ))}
          {activeActions.size > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={() => setActiveActions(new Set())}>
              Clear
            </button>
          )}
        </div>

        {/* Target type */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', minWidth: 72 }}>Target type</span>
          {TARGET_TYPE_CHIPS.map(t => (
            <button
              key={t}
              className={`btn btn-sm ${activeTargets.has(t) ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => toggleChip(activeTargets, t, setActiveTargets)}
            >
              {t}
            </button>
          ))}
          {activeTargets.size > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={() => setActiveTargets(new Set())}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Entry count */}
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
        Showing {visibleEntries.length} of {entries.length} entries
        {(activeRoles.size > 0 || activeActions.size > 0 || activeTargets.size > 0) && ' (filtered)'}
      </div>

      {/* Empty state */}
      {visibleEntries.length === 0 && !endpointMissing && (
        <div className="empty-state">
          {entries.length === 0
            ? 'Waiting for audit log entries…'
            : 'No entries match the active filters.'}
        </div>
      )}

      {/* Table */}
      {visibleEntries.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Role</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Changes</th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map((entry, idx) => {
                const key = entry.log_id ?? String(idx);
                return (
                  <EntryRow
                    key={key}
                    entry={entry}
                    isNew={!!entry._isNew}
                    expanded={expanded.has(key)}
                    onToggle={() => toggleExpanded(key)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        @keyframes auditSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .audit-row-new {
          animation: auditSlideIn 0.35s ease-out both;
          background: rgba(108, 142, 245, 0.07);
        }
      `}</style>
    </div>
  );
}
